import { createContext, useContext } from 'react';
import { Product as ProductConfig, Page as PageConfig } from '#config/products';
import { Peer } from '#core/PageQuery';

export type ProductPage = PageConfig & {
  routerPath: string;
  upsellComponent?: React.FC;
  url: string;
  auth: {
    action: string;
    userHasAccess: boolean;
  };
};

export type Product = Omit<ProductConfig, 'pages'> & {
  auth: {
    userHasAccess: boolean;
  };
  pages: ProductPage[];
};

type PeerChangeDraft = {
  userId: string;
  username: string;
  fullName?: string | null;
  institutionIds: string[];
  peers?: Peer[] | null;
};

export type ProductsContextContract = {
  products: Product[];
  productPeers: (
    productName: string,
  ) => {
    id: string;
    institutionId: string;
    name: string;
    country: string;
    continent: string;
    logo: string;
  }[];
  productGroups: (productName: string) => string[];
  peersInfo: (
    productName: string,
  ) => {
    supportsPeers: boolean;
    supportsChangingPeers: boolean;
    peerChangeOpen: boolean;
    lastPeerChangeDate: Date | null;
    maxPeerCount: number;
    peerChangeIntervalWeeks: number | null;
    peerChangeDraft?: PeerChangeDraft | null;
  };
};

const defaultProductsContext = {
  products: [],
  productPeers: () => [],
  productGroups: () => [],
  peersInfo: () => ({
    supportsPeers: false,
    supportsChangingPeers: false,
    peerChangeOpen: false,
    lastPeerChangeDate: null,
    maxPeerCount: -1,
    peerChangeIntervalWeeks: null,
    peerChangeDraft: undefined,
  }),
};

const ProductsContext = createContext<ProductsContextContract>(
  defaultProductsContext,
);

export const useProducts = () =>
  useContext<ProductsContextContract>(ProductsContext);

export default ProductsContext;
